import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { SeasonTypeMaintenance } from 'src/app/pages/fleets/interfaces/TyreMaintenanceRecord.interface';
import { FunnelService } from 'src/app/pages/new-service/services/funnel.service';
import { TyreMeasureData } from 'src/app/pages/fleets/detail-fleet/components/maintenance-record-detail/models/TyreMeasureData.model.model';

interface TyreModelResponse {
  Tyres?: Array<{
    Marca: string;
    Modelo: string;
  }>;
}

type DimensionField = 'Width' | 'Series' | 'Diameter' | 'LoadIndex' | 'SpeedIndex';

interface ModelsQueryParams {
  width: string;
  series: string;
  diameter: string;
  loadIndex: string;
  speedIndex: string;
  isAntiFlat: boolean;
  season: string;
}

@Component({
  selector: 'app-wheel-characteristics-input',
  templateUrl: './wheel-characteristics-input.component.html',
  styleUrls: ['./wheel-characteristics-input.component.scss']
})
export class WheelCharacteristicsInputComponent implements OnInit, OnDestroy, OnChanges {
  @Input() SeasonShow: boolean = true;
  @Input() Season!: SeasonTypeMaintenance;
  @Input() Width: string = '';
  @Input() Series: string = '';
  @Input() Diameter: string = '';
  @Input() LoadIndex: string = '';
  @Input() SpeedIndex: string = '';
  @Input() Brand: string = '';
  @Input() Model: string = '';
  @Input() IsAntiFlat: boolean = false;
  @Input() DidWeAlignTyres: boolean = false;
  @Input() DidWePermuteTyres: boolean = false;
  @Input() DotCode: string = '';
  @Input() wheelMeasureData: TyreMeasureData | undefined;
  @Input() EditMode: boolean = false;
  @Input() TyreReadingTitle: boolean = false;
  @Input() BrandMdelTitle: boolean = false;
  @Input() DotCodeTitle: boolean = false;

  @Output() SeasonEmitter = new EventEmitter<SeasonTypeMaintenance>();
  @Output() WidthEmitter = new EventEmitter<string>();
  @Output() SeriesEmitter = new EventEmitter<string>();
  @Output() DiameterEmitter = new EventEmitter<string>();
  @Output() LoadIndexEmitter = new EventEmitter<string>();
  @Output() SpeedIndexEmitter = new EventEmitter<string>();
  @Output() BrandEmitter = new EventEmitter<string>();
  @Output() ModelEmitter = new EventEmitter<string>();
  @Output() IsAntiFlatEmitter = new EventEmitter<boolean>();
  @Output() DidWeAlignTyresEmitter = new EventEmitter<boolean>();
  @Output() DidWePermuteTyresEmitter = new EventEmitter<boolean>();
  @Output() DotCodeEmitter = new EventEmitter<string>();

  readonly SeasonTypeMaintenance = SeasonTypeMaintenance;

  WidthArray: string[] = [];
  SeriesArray: string[] = [];
  DiameterArray: string[] = [];
  LoadIndexArray: string[] = [];
  SpeedIndexArray: string[] = [];
  BrandArray: string[] = [];

  BrandModelMap: Map<string, string[]> = new Map();
  loadingModels: boolean = false;
  private modelsRequestCompleted: boolean = false;
  private lastModelsQueryKey: string | null = null;
  modelsErrorKey: string = '';
  private modelsCache = new Map<string, TyreModelResponse>();

  private destroy$ = new Subject<void>();
  private isDestroyed = false;

  constructor(private readonly funnelService: FunnelService) { }

  ngOnInit(): void {
    if (this.wheelMeasureData) {
      this.setWidthArray();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['wheelMeasureData'] && this.wheelMeasureData) {
      this.setWidthArray();
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  setSeason(season: SeasonTypeMaintenance): void {
    this.Season = season;
    this.SeasonEmitter.emit(this.Season);
    this.resetModelsStateOnInputChange();
    this.triggerModelsSearchIfReady();
  }

  setWidth(width: string): void {
    this.Width = width;
    this.WidthEmitter.emit(this.Width);
    this.resetModelsStateOnInputChange();
    this.setSeriesArray();
    this.triggerModelsSearchIfReady();
  }

  setSeries(series: string): void {
    this.Series = series;
    this.SeriesEmitter.emit(this.Series);
    this.resetModelsStateOnInputChange();
    this.setDiameterArray();
    this.triggerModelsSearchIfReady();
  }

  setDiameter(diameter: string): void {
    this.Diameter = diameter;
    this.DiameterEmitter.emit(this.Diameter);
    this.resetModelsStateOnInputChange();
    this.setLoadIndexArray();
    this.triggerModelsSearchIfReady();
  }

  setLoadIndex(loadIndex: string): void {
    this.LoadIndex = loadIndex;
    this.LoadIndexEmitter.emit(this.LoadIndex);
    this.resetModelsStateOnInputChange();
    this.setSpeedIndexArray();
    this.triggerModelsSearchIfReady();
  }

  setSpeedIndex(speedIndex: string): void {
    this.SpeedIndex = speedIndex;
    this.SpeedIndexEmitter.emit(this.SpeedIndex);
    this.resetModelsStateOnInputChange();
    this.triggerModelsSearchIfReady();
  }

  setBrand(brand: string): void {
    this.Brand = brand;
    this.BrandEmitter.emit(this.Brand);
    this.updateModelFromBrand(brand);
  }

  setModel(model: string): void {
    this.Model = model;
    this.ModelEmitter.emit(this.Model);
  }

  changeIsAntiFlat(): void {
    this.IsAntiFlat = !this.IsAntiFlat;
    this.IsAntiFlatEmitter.emit(this.IsAntiFlat);
    this.resetModelsStateOnInputChange();
    this.triggerModelsSearchIfReady();
  }

  changeDidWeAlignTyres(): void {
    this.DidWeAlignTyres = !this.DidWeAlignTyres;
    this.DidWeAlignTyresEmitter.emit(this.DidWeAlignTyres);
  }

  changeDidWePermuteTyres(): void {
    this.DidWePermuteTyres = !this.DidWePermuteTyres;
    this.DidWePermuteTyresEmitter.emit(this.DidWePermuteTyres);
  }

  setDotCode(dotCode: string): void {
    const sanitized = dotCode.replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase();
    this.DotCode = sanitized;
    this.DotCodeEmitter.emit(this.DotCode);
  }

  get hasBasicDimensions(): boolean {
    return !!(this.Width && this.Series && this.Diameter && this.LoadIndex && this.SpeedIndex);
  }

  get hasBrandAndModel(): boolean {
    return !!(this.Brand && this.Model);
  }

  get isSeasonReady(): boolean {
    return true;
  }

  get canLoadModels(): boolean {
    return this.hasBasicDimensions;
  }

  get currentStep(): 1 | 2 | 3 {
    if (!this.hasBasicDimensions) {
      return 1;
    }
    if (!this.hasBrandAndModel) {
      return 2;
    }
    return 3;
  }

  private setWidthArray(): void {
    if (!this.wheelMeasureData?.Width) {
      return;
    }
    this.WidthArray = this.uniqueSorted(Object.keys(this.wheelMeasureData.Width));
    if (this.Width) {
      this.setWidth(this.Width);
    }
  }

  private setSeriesArray(): void {
    if (!this.wheelMeasureData?.Width?.[this.Width]?.Series) {
      this.SeriesArray = [];
      return;
    }
    this.SeriesArray = this.uniqueSorted(Object.keys(this.wheelMeasureData.Width[this.Width].Series));
    this.selectSingleOrPreserve('Series', this.SeriesArray);
  }

  private setDiameterArray(): void {
    if (!this.wheelMeasureData?.Width?.[this.Width]?.Series?.[this.Series]?.Diameter) {
      this.DiameterArray = [];
      return;
    }
    this.DiameterArray = this.uniqueSorted(Object.keys(
      this.wheelMeasureData.Width[this.Width].Series[this.Series].Diameter
    ));
    this.selectSingleOrPreserve('Diameter', this.DiameterArray);
  }

  private setLoadIndexArray(): void {
    if (!this.wheelMeasureData?.Width?.[this.Width]?.Series?.[this.Series]?.Diameter?.[this.Diameter]?.LoadIndex) {
      this.LoadIndexArray = [];
      return;
    }
    this.LoadIndexArray = this.uniqueSorted(Object.keys(
      this.wheelMeasureData.Width[this.Width].Series[this.Series].Diameter[this.Diameter].LoadIndex
    ));
    this.selectSingleOrPreserve('LoadIndex', this.LoadIndexArray);
  }

  private setSpeedIndexArray(): void {
    if (!this.wheelMeasureData?.Width?.[this.Width]?.Series?.[this.Series]?.Diameter?.[this.Diameter]?.LoadIndex?.[this.LoadIndex]?.SpeedIndex) {
      this.SpeedIndexArray = [];
      return;
    }
    this.SpeedIndexArray = this.uniqueSorted(
      this.wheelMeasureData.Width[this.Width].Series[this.Series].Diameter[this.Diameter].LoadIndex[this.LoadIndex].SpeedIndex
    );
    this.selectSingleOrPreserve('SpeedIndex', this.SpeedIndexArray);
  }

  private selectSingleOrPreserve(field: DimensionField, array: string[]): void {
    if (array.length === 1) {
      this.selectDimensionField(field, array[0]);
    } else {
      const currentValue = this.getCurrentDimensionValue(field);
      const preservedValue = currentValue && array.includes(currentValue) ? currentValue : '';
      this.selectDimensionField(field, preservedValue);
    }
  }

  private selectDimensionField(field: DimensionField, value: string): void {
    switch (field) {
      case 'Series':
        this.setSeries(value);
        break;
      case 'Diameter':
        this.setDiameter(value);
        break;
      case 'LoadIndex':
        this.setLoadIndex(value);
        break;
      case 'SpeedIndex':
        this.setSpeedIndex(value);
        break;
    }
  }

  private getCurrentDimensionValue(field: DimensionField): string {
    switch (field) {
      case 'Series':
        return this.Series;
      case 'Diameter':
        return this.Diameter;
      case 'LoadIndex':
        return this.LoadIndex;
      case 'SpeedIndex':
        return this.SpeedIndex;
      default:
        return '';
    }
  }

  private setBrandArray(): void {
    this.BrandArray = this.uniqueSorted(Array.from(this.BrandModelMap.keys()));
    if (this.BrandArray.length === 0) {
      return;
    }

    if (!this.EditMode && this.BrandArray.length > 0) {
      this.setBrand(this.BrandArray[0]);
    } else {
      const preservedBrand = this.Brand && this.BrandArray.includes(this.Brand) ? this.Brand : '';
      this.setBrand(preservedBrand);
    }
  }

  private updateModelFromBrand(brand: string): void {
    const modelArray = this.BrandModelMap.get(brand) || [];
    if (!this.EditMode && modelArray.length > 0) {
      this.setModel(modelArray[0]);
    } else {
      const preservedModel = this.Model && modelArray.includes(this.Model) ? this.Model : '';
      this.setModel(preservedModel);
    }
  }

  private loadModels(): void {
    if (!this.canLoadModels) {
      return;
    }

    const queryKey = this.buildModelsQueryKey();
    if (queryKey === this.lastModelsQueryKey) {
      return;
    }
    this.lastModelsQueryKey = queryKey;

    const cached = this.modelsCache.get(queryKey);
    if (cached) {
      if (cached?.Tyres) {
        this.processTyreModels(cached.Tyres);
      }
      this.modelsRequestCompleted = true;
      this.loadingModels = false;
      return;
    }

    this.loadModelsRequest()
      .then((data: TyreModelResponse) => {
        if (this.isDestroyed) {
          return;
        }
        if (data?.Tyres) {
          this.processTyreModels(data.Tyres);
        }
        this.modelsCache.set(queryKey, data);
        this.modelsRequestCompleted = true;
      })
      .catch((error: unknown) => {
        if (this.isDestroyed) {
          return;
        }
        console.error('Error loading models:', error);
        this.modelsErrorKey = 'WHEEL_CHARACTERISTICS.MODELS_ERROR';
        this.modelsRequestCompleted = true;
      })
      .finally(() => {
        if (this.isDestroyed) {
          return;
        }
        this.loadingModels = false;
      });
  }

  private processTyreModels(tyres: Array<{ Marca: string; Modelo: string }>): void {
    this.BrandModelMap.clear();
    if (tyres.length > 0 && !this.EditMode) {
      this.Brand = '';
      this.Model = '';
    }

    tyres.forEach((tyre) => {
      const existingModels = this.BrandModelMap.get(tyre.Marca) || [];
      const nextModels = this.uniqueSorted([...existingModels, tyre.Modelo]);
      this.BrandModelMap.set(tyre.Marca, nextModels);
    });

    this.setBrandArray();
  }

  private loadModelsRequest(): Promise<TyreModelResponse> {
    this.loadingModels = true;
    this.modelsRequestCompleted = false;
    this.modelsErrorKey = '';

    const seasonString = this.convertSeasonToString(this.Season);

    return firstValueFrom(
      this.funnelService.getTyresModels(
        1,
        this.Width,
        this.Series,
        this.Diameter,
        this.LoadIndex,
        this.SpeedIndex,
        this.IsAntiFlat,
        false, false, false, false, false, false, false, false, false, false,
        seasonString
      ).pipe(take(1), takeUntil(this.destroy$))
    );
  }

  private convertSeasonToString(season: SeasonTypeMaintenance): string {
    const seasonMap: Record<SeasonTypeMaintenance, string> = {
      [SeasonTypeMaintenance.SUMMER]: 'SUMMER',
      [SeasonTypeMaintenance.WINTER]: 'WINTER',
      [SeasonTypeMaintenance.ALL_SEASON]: 'ALL SEASONS'
    };
    return seasonMap[season] || '';
  }

  get showNoModelsMessage(): boolean {
    return this.hasBasicDimensions
      && !this.loadingModels
      && this.modelsRequestCompleted
      && !this.modelsErrorKey
      && this.BrandModelMap.size === 0;
  }

  get availableModelsCount(): number {
    let count = 0;
    this.BrandModelMap.forEach((models) => {
      count += models.length;
    });
    return count;
  }

  get showModelsError(): boolean {
    return !!this.modelsErrorKey && !this.loadingModels;
  }

  get dotCodeErrorKey(): string | null {
    if (!this.DotCode) {
      return null;
    }
    if (!/^[A-Za-z0-9]{4}$/.test(this.DotCode)) {
      return 'WHEEL_CHARACTERISTICS.DOT_CODE_INVALID';
    }
    return null;
  }

  retryLoadModels(): void {
    this.triggerModelsSearchIfReady(true);
  }

  private resetModelsStateOnInputChange(): void {
    this.modelsRequestCompleted = false;
    this.modelsErrorKey = '';
    this.BrandModelMap.clear();
    this.BrandArray = [];
    this.lastModelsQueryKey = null;
  }

  private triggerModelsSearchIfReady(force: boolean = false): void {
    if (!this.canLoadModels) {
      return;
    }

    if (force) {
      this.lastModelsQueryKey = null;
    }

    this.loadModels();
  }

  private buildModelsQueryKey(): string {
    const params: ModelsQueryParams = {
      width: this.Width.trim(),
      series: this.Series.trim(),
      diameter: this.Diameter.trim(),
      loadIndex: this.LoadIndex.trim(),
      speedIndex: this.SpeedIndex.trim(),
      isAntiFlat: this.IsAntiFlat,
      season: this.convertSeasonToString(this.Season),
    };

    return [
      params.width,
      params.series,
      params.diameter,
      params.loadIndex,
      params.speedIndex,
      String(params.isAntiFlat),
      params.season,
    ].join('|');
  }

  private uniqueSorted(values: string[]): string[] {
    return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }
}
